/**
 * Logger Utility - Sistema de logging centralizado
 *
 * Solo loggea en desarrollo (import.meta.env.DEV).
 * En producción, los logs se omiten automáticamente.
 *
 * @module utils/logger
 *
 * @example
 * import { logger } from '@/utils/logger';
 *
 * logger.debug('ModuleName', 'mensaje', { data });
 * logger.info('ModuleName', 'mensaje', { data });
 * logger.warn('ModuleName', 'mensaje', { data });
 * logger.error('ModuleName', 'mensaje', { error });
 *
 * // Output:
 * // 🔍 [DEBUG 10:30:45.123] [AuthService] User logged in { user_id: '123' }
 */

const COLORS = {
  DEBUG: 'color: #6B7280', // gray
  INFO: 'color: #3B82F6',  // blue
  WARN: 'color: #F59E0B',  // amber
  ERROR: 'color: #EF4444', // red
};

const ICONS = {
  DEBUG: '🔍',
  INFO: 'ℹ️',
  WARN: '⚠️',
  ERROR: '❌',
};

/**
 * Obtiene timestamp formateado HH:MM:SS.mmm
 * @returns {string} Timestamp formateado
 */
function getTimestamp() {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const ms = String(now.getMilliseconds()).padStart(3, '0');
  return `${hours}:${minutes}:${seconds}.${ms}`;
}

/**
 * Función base de logging
 * @param {string} level - Nivel de log (DEBUG, INFO, WARN, ERROR)
 * @param {string} module - Nombre del módulo que loggea
 * @param {string} message - Mensaje a loggear
 * @param {Object} data - Datos adicionales
 */
function log(level, module, message, data = {}) {
  // Solo loggear en desarrollo
  if (!import.meta.env.DEV) return;

  const timestamp = getTimestamp();
  const prefix = `${ICONS[level]} [${level} ${timestamp}] [${module}]`;

  // Formatear output
  const has_data = data && Object.keys(data).length > 0;

  if (level === 'ERROR') {
    if (has_data) {
      console.error(`%c${prefix} ${message}`, COLORS[level], data);
    } else {
      console.error(`%c${prefix} ${message}`, COLORS[level]);
    }

    // Stack trace para errores
    if (data?.error instanceof Error) {
      console.error(`%c   Stack:`, 'color: #9CA3AF', data.error.stack);
    }
  } else if (level === 'WARN') {
    if (has_data) {
      console.warn(`%c${prefix} ${message}`, COLORS[level], data);
    } else {
      console.warn(`%c${prefix} ${message}`, COLORS[level]);
    }
  } else {
    if (has_data) {
      console.log(`%c${prefix} ${message}`, COLORS[level], data);
    } else {
      console.log(`%c${prefix} ${message}`, COLORS[level]);
    }
  }
}

/**
 * Logger público
 *
 * @example
 * // Debug - información de desarrollo
 * logger.debug('UserService', 'Fetching user profile', { user_id: '123' });
 *
 * // Info - eventos importantes exitosos
 * logger.info('AuthSlice', 'User logged in', { email: 'user@example.com' });
 *
 * // Warn - algo raro pero no crítico
 * logger.warn('ApiClient', 'Slow request detected', { duration_ms: 3000 });
 *
 * // Error - algo falló
 * logger.error('PaymentService', 'Payment failed', { error: err });
 */
export const logger = {
  /**
   * Log de nivel DEBUG
   * @param {string} module - Módulo que genera el log
   * @param {string} message - Mensaje descriptivo
   * @param {Object} [data] - Datos adicionales
   */
  debug: (module, message, data) => log('DEBUG', module, message, data),

  /**
   * Log de nivel INFO
   * @param {string} module - Módulo que genera el log
   * @param {string} message - Mensaje descriptivo
   * @param {Object} [data] - Datos adicionales
   */
  info: (module, message, data) => log('INFO', module, message, data),

  /**
   * Log de nivel WARN
   * @param {string} module - Módulo que genera el log
   * @param {string} message - Mensaje descriptivo
   * @param {Object} [data] - Datos adicionales
   */
  warn: (module, message, data) => log('WARN', module, message, data),

  /**
   * Log de nivel ERROR
   * @param {string} module - Módulo que genera el log
   * @param {string} message - Mensaje descriptivo
   * @param {Object} [data] - Datos adicionales (puede incluir { error: Error })
   */
  error: (module, message, data) => log('ERROR', module, message, data),

  /**
   * Log de grupo (para flujos multi-step)
   * Agrupa logs relacionados bajo un label colapsable
   *
   * @param {string} module - Módulo que genera el log
   * @param {string} label - Etiqueta del grupo
   * @param {Function} fn - Función a ejecutar dentro del grupo
   *
   * @example
   * logger.group('Auth', 'Login Flow', () => {
   *   logger.debug('Auth', 'Step 1: Validate credentials');
   *   logger.debug('Auth', 'Step 2: Get session');
   *   logger.info('Auth', 'Login complete');
   * });
   */
  group: (module, label, fn) => {
    if (!import.meta.env.DEV) {
      fn();
      return;
    }

    console.group(`📦 [${module}] ${label}`);
    try {
      fn();
    } finally {
      console.groupEnd();
    }
  },
};

export default logger;
